import Pagination from '../../components/Common/Pagination';
import React, { useState, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import { CustomCalendar } from '../../components/Common/CustomCalendar';

interface Batch {
  id: number | string;
  name: string;
  skuId: string;
  skuName: string;
  mrp: string;
  manufactureDate: string;
  manufactureTime: string;
  manufactureTime2?: string;
  status: string;
}

export const BatchesList: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([
    {
      id: 'ID',
      name: 'Name',
      skuId: 'SKU ID',
      skuName: 'SKU Name',
      mrp: 'MRP',
      manufactureDate: 'Manufacture Date',
      manufactureTime: 'Manufacture Time',
      manufactureTime2: 'Manufacture Time',
      status: 'Status',
    }
  ]);
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [_editingBatch, setEditingBatch] = useState<Batch | null>(null);

  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    selectedSku: '',
    mrp: '',
    manufactureTime: '',
    manufactureDate: '2026-03-28',
    expiryDate: '2026-03-28',
    showAttendance: false,
  });

  const activeCounts = 445; // Fixed mock count as shown in figma
  const inactiveCounts = 445; // Fixed mock count as shown in figma

  // Filtering Logic
  const filteredBatches = useMemo(() => {
    return batches.filter((batch) => {
      if (activeTab === 'Active' && batch.status !== 'Active') return false;
      if (activeTab === 'Inactive' && batch.status !== 'Inactive') return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          batch.name.toLowerCase().includes(query) ||
          batch.skuName.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [batches, activeTab, searchQuery]);

  // Paginated Items
  const paginatedBatches = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredBatches.slice(startIndex, startIndex + entriesLimit);
  }, [filteredBatches, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingBatch(null);
    setFormData({
      name: '',
      selectedSku: '',
      mrp: '',
      manufactureTime: '',
      manufactureDate: '2026-03-28',
      expiryDate: '2026-03-28',
      showAttendance: false,
    });
    setView('add');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Name is required.');
      return;
    }

    const newBatch: Batch = {
      id: batches.length > 0 ? Math.max(...batches.map(b => typeof b.id === 'number' ? b.id : 0)) + 1 : 1,
      name: formData.name,
      skuId: formData.selectedSku ? 'SKU-' + formData.selectedSku.substring(0, 3).toUpperCase() : '-',
      skuName: formData.selectedSku || '-',
      mrp: formData.mrp || '0.00',
      manufactureDate: formData.manufactureDate,
      manufactureTime: formData.manufactureTime || '-',
      manufactureTime2: formData.manufactureTime || '-',
      status: 'Active',
    };

    setBatches([...batches, newBatch]);
    setView('list');
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button 
            type="button"
            onClick={() => setView('list')}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Add Batch
          </h1>
        </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Name</label>
              <input 
                type="text" 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Selected SKU */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Selected SKU</label>
              <div className="relative">
                <select
                  value={formData.selectedSku}
                  onChange={(e) => setFormData({...formData, selectedSku: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-700 appearance-none bg-white"
                >
                  <option value="">Select SKUs From live</option>
                  <option value="GOW. GOLD MILK 500ML">GOW. GOLD COW MILK 500 ML</option>
                  <option value="GOW. COW GHEE 1LTR">GOW. COW GHEE 1 LTR TETRA</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* MRP */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">MRP</label>
              <input 
                type="text" 
                value={formData.mrp}
                onChange={(e) => setFormData({...formData, mrp: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white" 
              />
            </div>

            {/* Manufacture Time */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Manufacture Time</label>
              <input 
                type="text" 
                placeholder="Select SKUs From live"
                value={formData.manufactureTime}
                onChange={(e) => setFormData({...formData, manufactureTime: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white" 
              />
            </div>

            {/* Manufacture Date */}
            <CustomCalendar
              label="Manufacture Date"
              value={formData.manufactureDate}
              onChange={(val) => setFormData({ ...formData, manufactureDate: val })}
            />

            {/* Expiry Date */}
            <CustomCalendar
              label="Expiry Date"
              value={formData.expiryDate}
              minDate={formData.manufactureDate}
              onChange={(val) => setFormData({ ...formData, expiryDate: val })}
            />

            {/* Show Attendance Checkbox */}
            <div className="col-span-1 md:col-span-2 flex items-center gap-2 select-none pt-2">
              <input 
                type="checkbox" 
                id="showAttendance"
                checked={formData.showAttendance}
                onChange={(e) => setFormData({...formData, showAttendance: e.target.checked})}
                className="rounded go_erp-text focus:ring-[#C62828] w-4 h-4 cursor-pointer"
              />
              <label htmlFor="showAttendance" className="text-xs font-bold text-slate-700 cursor-pointer">Show Attendance</label>
            </div>
          </div>

        </form>
          {/* Submit */}
          <div className="flex justify-center pt-4">
            <button 
              type="submit" 
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20"
            >
              Submit
            </button>
          </div>
      </div>
    );
  }

  const handleExportCSV = () => {
    const headers = ['ID', 'Batch Name', 'SKU ID', 'SKU Name', 'MRP', 'Mfg Date', 'Mfg Time', 'Status'];
    const rows = filteredBatches.map((b) => [
      b.id,
      b.name,
      b.skuId,
      b.skuName,
      b.mrp,
      b.manufactureDate,
      b.manufactureTime,
      b.status,
    ]);
    exportToCSV(`batches_${activeTab.toLowerCase()}`, headers, rows);
  };

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Batches
          </h1>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${
                  isActive
                    ? 'go_erp-bg text-white shadow-sm'
                    : 'text-slate-655 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Batch
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold">{inactiveCounts}</span></span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 px-4 outline-none focus:border-slate-400 w-44 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">SKU ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">SKU Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">MRP</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Manufacture Date</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Manufacture Time</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Manufacture Time</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {paginatedBatches.length > 0 ? (
                paginatedBatches.map((batch) => (
                  <tr key={batch.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.id}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.name}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.skuId}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.skuName}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.mrp}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.manufactureDate}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.manufactureTime}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.manufactureTime2 || batch.manufactureTime}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{batch.status}</td>
                    <td className="px-4 py-4 text-xs text-left">
                      {batch.id === 'ID' ? (
                        <span className="text-[#828282]">Actions</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F9C327] hover:bg-[#e2af20] text-slate-900 font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer" title="View Details">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                            View
                          </button>
                          <button className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer" title="Edit">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-6 py-14 text-center text-xs font-semibold text-slate-400">
                    No Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredBatches.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default BatchesList;
